/**
 * useTableMetadata Hook
 *
 * Fetches table metadata (columns, constraints, indexes) for FK detection.
 * Uses TanStack Query for caching and deduplication.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TableMetadata, ConstraintMetadata, LooseRelationship } from '../../../../types/electron';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import { matchColumnName } from '../../common/utils/columnNameMatch';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';
import { compareFkConstraints, realFkOutranksLoose } from '../../common/utils/looseFkResolver';
import { classifyJsonColumn } from '../../../sql-resolver/json/classify/classifyJsonColumn';
import type { JsonClassification } from '../../../sql-resolver/types/schema';
import type { SqlEngine } from '../../../sql-grammar/model';
import type { DatabaseEngineId } from '../../../../core/engines';
import { fetchTableMetadata, TABLE_METADATA_STALE_TIME_MS, tableMetadataQueryKey } from './tableMetadataQuery';

const SQL_ENGINE_MAP: Partial<Record<DatabaseEngineId, SqlEngine>> = {
  mssql: 'mssql',
  pgsql: 'postgres',
  mysql: 'mysql',
  oracle: 'oracle',
  sqlite: 'sqlite',
};

function formatColumnDataType(column: TableMetadata['columns'][number]): string {
  const rawType = column.dataType?.trim();
  if (!rawType) return 'unknown';

  // Some engines (e.g., PostgreSQL via `format_type`) already include parameters in the string.
  if (rawType.includes('(')) return rawType;

  const normalized = rawType.toLowerCase();

  // Types with length parameter
  const lengthTypes = new Set(['varchar', 'nvarchar', 'char', 'nchar', 'binary', 'varbinary']);
  if (lengthTypes.has(normalized)) {
    const maxLength = column.maxLength;
    if (maxLength === null || maxLength === -1) {
      return `${rawType}(max)`;
    }
    if (maxLength !== undefined && maxLength !== null && maxLength > 0) {
      // In MSSQL, N* types store 2 bytes per character (sys.columns.max_length is in bytes).
      const displayLength = normalized.startsWith('n') && maxLength > 1 ? Math.floor(maxLength / 2) : maxLength;
      return `${rawType}(${displayLength})`;
    }
    return rawType;
  }

  // Types with precision and scale
  const precisionScaleTypes = new Set(['decimal', 'numeric']);
  if (precisionScaleTypes.has(normalized)) {
    const precision = column.precision;
    const scale = column.scale;
    if (precision !== undefined && precision !== null) {
      if (scale !== undefined && scale !== null && scale > 0) {
        return `${rawType}(${precision}, ${scale})`;
      }
      return `${rawType}(${precision})`;
    }
    return rawType;
  }

  return rawType;
}

type UseTableMetadataParams = {
  connectionId: string;
  database: string;
  schema: string;
  tableName: string;
  enabled: boolean;
  /**
   * Engine ID of the connection. When provided, columns get a populated
   * `jsonClassification` via `classifyJsonColumn` so JSON-aware filter
   * operator dropdowns in Table Explorer can trim to engine-correct sets
   * (Slice E3). When omitted, columns surface with `jsonClassification`
   * undefined and consumers fall back to the legacy data-type-only path.
   */
  engineId?: DatabaseEngineId;
};

/**
 * Parsed FK info for a column
 */
export type ColumnForeignKeyInfo = {
  constraintName: string;
  referencedSchema?: string;
  referencedTable: string;
  referencedColumn: string;
  /** True when this entry came from a user-declared loose relationship in MetaInfo, not a real FK constraint. */
  isLoose?: boolean;
  /**
   * True when this real-FK entry is the column's pairing inside a MULTI-column
   * constraint (the column is one of its members). Such an entry yields to a
   * loose declaration on the column (`realFkOutranksLoose`).
   */
  isComposite?: boolean;
};

/**
 * A table's FK targets keyed by the column's EXACT name — the table's own
 * spelling when the map was built with its column names (`columnNames`, kept
 * on the map for `lookupForeignKey`). Never by `normalizeColumnName`, which
 * folds `"ref-id"` and `"ref id"` (or `"Ref"` and `ref`) into one key and so
 * gave one column the other's target. Read it with `lookupForeignKey`.
 */
export type ForeignKeyMap = Map<string, ColumnForeignKeyInfo> & {
  /** The table's column names the keys are spelled as, when the map was built with them. */
  readonly columnNames?: readonly string[];
};

/**
 * `column`'s FK entry: its exact key first, else the key of the one table
 * column (`map.columnNames`, or the map's own keys when those are unknown)
 * `column` names ignoring case (`matchColumnName`) — so a driver or a
 * declaration spelling another case still finds it, an ambiguous or distinct
 * name never borrows another column's target.
 */
export function lookupForeignKey(map: ForeignKeyMap | undefined, column: string): ColumnForeignKeyInfo | undefined {
  if (!map) return undefined;
  const exact = map.get(column);
  if (exact) return exact;
  const key = matchColumnName(column, map.columnNames ?? map.keys());
  return key === undefined ? undefined : map.get(key);
}

/**
 * Extract FK info from constraints into a column-keyed map (`ForeignKeyMap`:
 * the exact column name, the table's own spelling when `columnNames` is given).
 * A column's target is the first real FK constraint covering it in
 * `compareFkConstraints` order — a single-column constraint before a
 * composite one, then by constraint name — the rule every FK surface shares.
 * Loose relationships (declared via MetaInfo) are appended after real FKs; a
 * real SINGLE-column FK on the same column wins on conflict, while a column
 * that is only a member of a composite FK yields to its loose declaration
 * (`realFkOutranksLoose`).
 */
export function buildForeignKeyMap(
  constraints: ConstraintMetadata[] | undefined,
  looseRelationships: readonly LooseRelationship[] = [],
  columnNames?: readonly string[],
): ForeignKeyMap {
  // Empty column metadata means unknown, not "no columns".
  const known = columnNames && columnNames.length > 0 ? columnNames : undefined;
  const fkMap: ForeignKeyMap = Object.assign(new Map<string, ColumnForeignKeyInfo>(), known ? { columnNames: known } : {});
  // A constraint / declaration spelled another way keys the table's own spelling.
  const keyOf = (column: string): string => (known ? matchColumnName(column, known) : undefined) ?? column;

  const foreignKeys = (constraints ?? [])
    .filter((c) => c.type === 'FOREIGN KEY' && !!c.referencedTable)
    .sort((a, b) =>
      compareFkConstraints({ name: a.name, columnCount: a.columns.length }, { name: b.name, columnCount: b.columns.length }),
    );
  for (const constraint of foreignKeys) {
    const isComposite = !realFkOutranksLoose(constraint.columns.length);

    // Map each column in the FK constraint to its referenced column
    constraint.columns.forEach((col, index) => {
      const referencedColumn = constraint.referencedColumns?.[index];
      if (!referencedColumn) return;
      const key = keyOf(col);
      // The first constraint in precedence order is the column's target.
      if (fkMap.has(key)) return;
      fkMap.set(key, {
        constraintName: constraint.name,
        referencedSchema: constraint.referencedSchema,
        referencedTable: constraint.referencedTable!,
        referencedColumn,
        ...(isComposite ? { isComposite: true } : {}),
      });
    });
  }

  // Append loose relationships — a single-column real FK wins on conflict,
  // and the first declaration on a column wins over later ones.
  for (const rel of looseRelationships) {
    const key = keyOf(rel.fromColumn);
    const existing = fkMap.get(key);
    if (existing && (existing.isLoose || !existing.isComposite)) continue;
    fkMap.set(key, {
      constraintName: `loose:${rel.id}`,
      referencedSchema: rel.toSchema,
      referencedTable: rel.toTable,
      referencedColumn: rel.toColumn,
      isLoose: true,
    });
  }

  return fkMap;
}

/**
 * Extract PK columns from constraints
 */
function getPrimaryKeyColumns(constraints: ConstraintMetadata[] | undefined): Set<string> {
  const pkColumns = new Set<string>();

  if (!constraints) return pkColumns;

  for (const constraint of constraints) {
    if (constraint.type === 'PRIMARY KEY') {
      constraint.columns.forEach((col) => pkColumns.add(col));
    }
  }

  return pkColumns;
}

/**
 * Extract identity columns from column metadata
 */
function getIdentityColumns(metadata: TableMetadata | undefined): Set<string> {
  const identityColumns = new Set<string>();

  if (!metadata?.columns) return identityColumns;

  for (const col of metadata.columns) {
    if (col.isIdentity) {
      identityColumns.add(col.name);
    }
  }

  return identityColumns;
}

/**
 * Hook result type
 */
export type TableMetadataResult = {
  metadata: TableMetadata | undefined;
  foreignKeyMap: ForeignKeyMap;
  primaryKeyColumns: Set<string>;
  identityColumns: Set<string>;
  /** All columns from table metadata - stable list for column selector/filters/sorts */
  allColumns: TableExplorerColumn[];
  isLoading: boolean;
  error: Error | null;
  /** Whether a metadata fetch is currently in flight. */
  isFetching: boolean;
  /**
   * Imperative refetch. The open-from-pin reconcile gate calls this to force a
   * FRESH metadata read (bypassing the 5-min `staleTime`) so reconciliation
   * never trusts a possibly-stale cache entry. Ordinary tabs never call it, so
   * global cache behavior is unchanged.
   *
   * The resolved result carries the refetch's OWN outcome (L2): on error,
   * TanStack keeps the previous cache entry in `data`, so callers must branch
   * on `isError` (via `freshColumnsFromMetadataRefetch`) rather than trusting
   * `data`.
   */
  refetch: () => Promise<{ data: TableMetadata | undefined; isError: boolean }>;
};

/**
 * Hook to fetch table metadata with FK and PK info
 */
export function useTableMetadata(params: UseTableMetadataParams): TableMetadataResult {
  const { connectionId, database, schema, tableName, enabled, engineId } = params;

  const { data, isLoading, error, isFetching, refetch } = useQuery<TableMetadata | undefined>({
    queryKey: tableMetadataQueryKey({ connectionId, database, schema, tableName }),
    queryFn: () => fetchTableMetadata({ connectionId, database, schema, tableName }),
    enabled: enabled && !!connectionId && !!tableName,
    staleTime: TABLE_METADATA_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  // Pull user-declared loose relationships from MetaInfo for this database
  const { data: metaInfo } = useMetaInfo(connectionId, database);

  // Filter to relationships originating from the current table.
  // Memoized so the FK map memo's dep is stable across MetaInfo refetches
  // when the relevant slice has not changed.
  const tableLoose = useMemo(
    () =>
      (metaInfo?.looseRelationships ?? []).filter(
        (r) => r.fromSchema === schema && r.fromTable === tableName,
      ),
    [metaInfo, schema, tableName],
  );

  // Memoize derived maps/sets to prevent render loops
  // These only change when the underlying data changes
  const columnNames = useMemo(() => data?.columns?.map((c) => c.name), [data?.columns]);
  const foreignKeyMap = useMemo(
    () => buildForeignKeyMap(data?.constraints, tableLoose, columnNames),
    [data?.constraints, tableLoose, columnNames]
  );

  const primaryKeyColumns = useMemo(
    () => getPrimaryKeyColumns(data?.constraints),
    [data?.constraints]
  );

  const identityColumns = useMemo(
    () => getIdentityColumns(data),
    [data]
  );

  // MetaInfo loose-JSON declarations for this database — used to surface
  // user-declared JSON columns through `classifyJsonColumn` (Slice E3).
  // Reuses the same `useMetaInfo` query already pulled above for loose FKs.
  const looseJsonDeclaredSet = useMemo(() => {
    const set = new Set<string>();
    if (!metaInfo?.jsonColumns) return set;
    const schemaLc = schema.toLowerCase();
    const tableLc = tableName.toLowerCase();
    for (const d of metaInfo.jsonColumns) {
      if (d.schema.toLowerCase() !== schemaLc) continue;
      if (d.table.toLowerCase() !== tableLc) continue;
      set.add(d.column.toLowerCase());
    }
    return set;
  }, [metaInfo?.jsonColumns, schema, tableName]);

  const sqlEngine = engineId ? SQL_ENGINE_MAP[engineId] : undefined;

  // Build all columns list from metadata - stable list for column selector/filters/sorts
  // This is independent of which columns are selected for display
  const allColumns = useMemo((): TableExplorerColumn[] => {
    if (!data?.columns) return [];

    return data.columns.map((col) => {
      const fkInfo = lookupForeignKey(foreignKeyMap, col.name);
      const dataType = formatColumnDataType(col);
      let jsonClassification: JsonClassification | undefined;
      if (sqlEngine) {
        const hasLoose = looseJsonDeclaredSet.has(col.name.toLowerCase());
        const cls = classifyJsonColumn(dataType, sqlEngine, {
          hasLooseJsonDeclaration: hasLoose,
        });
        if (cls.kind !== 'not-json') jsonClassification = cls;
      }
      return {
        name: col.name,
        dataType,
        nullable: col.isNullable,
        isPrimaryKey: primaryKeyColumns.has(col.name),
        isForeignKey: !!fkInfo,
        isLoose: !!fkInfo?.isLoose,
        isIdentity: col.isIdentity || false,
        referencedSchema: fkInfo?.referencedSchema,
        referencedTable: fkInfo?.referencedTable,
        referencedColumn: fkInfo?.referencedColumn,
        jsonClassification,
        isNativeEnumType: col.isEnum === true,
      };
    });
  }, [data?.columns, primaryKeyColumns, foreignKeyMap, sqlEngine, looseJsonDeclaredSet]);

  return {
    metadata: data,
    foreignKeyMap,
    primaryKeyColumns,
    identityColumns,
    allColumns,
    isLoading,
    error: error as Error | null,
    isFetching,
    refetch,
  };
}

/**
 * Returns an invalidator for cached table metadata. Call it after a schema
 * change (e.g. Table Designer apply) so any open Table Explorer refetches and
 * picks up the new columns/PK without an app restart. Invalidates at the
 * database level so renames are covered too.
 */
export function useInvalidateTableMetadata() {
  const qc = useQueryClient();
  return (connectionId: string, database: string) => {
    qc.invalidateQueries({ queryKey: ['table-metadata', connectionId, database] });
  };
}
